import { useMemo, type ReactNode } from 'react'
import { useFechamentoDaCompetencia } from '../../../api/queries/chamados'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { SumarioDaPagina, type SecaoDoSumario } from '../../../components/SumarioDaPagina'
import { TEXTOS } from '../../../lib/glossario'
import type { CompetenciaComDado } from '../../../types/chamado'
import { CarimboDeProcedencia } from '../CarimboDeProcedencia'
import { competenciaPorExtenso } from '../competencia'
import { SeletorDeCompetencia } from '../SeletorDeCompetencia'
import { AcoesDaCompetencia } from './AcoesDaCompetencia'
import { CartaoDeIndicador } from './CartaoDeIndicador'
import { CartaoDoIie } from './CartaoDoIie'
import { ConferenciaDoMes } from './ConferenciaDoMes'
import { ContagensDoMes } from './ContagensDoMes'
import { CriteriosDoTr } from './CriteriosDoTr'
import { indicadorSemNumero } from './indicadores'
import { LinkDoTrp } from './LinkDoTrp'
import { RedutorDaFatura } from './RedutorDaFatura'
import { SeloDaCompetencia } from './SeloDaCompetencia'
import { ValorDaFatura } from './ValorDaFatura'

/** As partes da coluna principal, na ordem em que aparecem. É a lista que o sumário oferece. */
const SECOES: SecaoDoSumario[] = [
  { id: 'indicadores', rotulo: 'Indicadores' },
  { id: 'desconto', rotulo: 'Desconto na nota' },
  { id: 'analise', rotulo: 'Análise automática' },
  { id: 'conta', rotulo: 'A conta e as regras' },
  { id: 'fontes', rotulo: 'Prazos e fontes' },
]

interface Props {
  competencias: CompetenciaComDado[]
  competencia: string
  aoEscolher: (competencia: string) => void
  /** O calendário, a sincronização e as cargas — o que não depende do mês escolhido. */
  fontes: ReactNode
}

/**
 * A apuração de uma competência, em duas colunas.
 *
 * **À direita, o mês e o que fazer com ele**: a escolha do mês, o selo (prévia ou fechado), fechar
 * ou dispensar, o valor faturado e o termo. **À esquerda, os números**, em seções com atalho.
 *
 * Antes era uma coluna só, e a ordem era a dos atos: quem abria a tela rolava por dois formulários
 * antes de ver o primeiro indicador, e depois por mais quatro mil pixels até as cargas. Lado a lado,
 * os atos continuam à vista enquanto se lê o número que eles assinam. Em tela estreita a coluna dos
 * atos vem primeiro — ela está antes no DOM, e é também a ordem de quem usa leitor de tela: quem
 * abre esta tela vem fechar o mês.
 *
 * O selo vem antes de tudo na coluna dos atos: ele responde "este número é prévia ou é o que vale?",
 * e essa resposta muda a leitura de cada linha ao lado. O valor vem ANTES do termo: é ele que decide
 * se a folha sai com reais, e quem clica em imprimir sem tê-lo registrado descobre a falta no papel.
 *
 * @param props as competências, a escolhida, o comando de trocar e o bloco das fontes
 * @returns a apuração do mês
 */
export function FechamentoDaCompetencia({ competencias, competencia, aoEscolher, fontes }: Props) {
  const { data: dados, isPending, isError, error } = useFechamentoDaCompetencia(competencia)

  const origens = useMemo(
    () => (dados?.apuracao.cobertura.porOrigem ?? []).map((linha) => linha.origem),
    [dados],
  )

  const seletor = (
    <div className="cartao p-4">
      <SeletorDeCompetencia competencias={competencias} valor={competencia} aoEscolher={aoEscolher} />
    </div>
  )

  if (isPending) {
    return (
      <Colunas lateral={seletor}>
        <div className="cartao px-4 py-5">
          <Esqueleto linhas={8} />
        </div>
      </Colunas>
    )
  }
  if (isError) {
    return (
      <Colunas lateral={seletor}>
        <AvisoDeErro erro={error} titulo={`Não foi possível apurar ${competenciaPorExtenso(competencia)}`} />
        {fontes}
      </Colunas>
    )
  }

  const fechamento = dados.apuracao
  const fechada = dados.competencia.situacao === 'FECHADA'

  const lateral = (
    <>
      {seletor}
      <SeloDaCompetencia situacao={dados.competencia} dispensa={dados.dispensa} />
      {/* Com o mês fechado e sem papel de administrador não sobra ação nenhuma, e o cartão ficaria
          uma caixa branca vazia no meio da coluna. O `:has` some com ele nesse caso. */}
      <div className="cartao p-4 [&:not(:has(button,p))]:hidden">
        <AcoesDaCompetencia
          competencia={competencia}
          situacao={dados.competencia}
          dispensa={dados.dispensa}
          chamadosSemJulgamento={fechamento.chamadosSemJulgamentoDoFiscal.length}
        />
      </div>
      <ValorDaFatura competencia={competencia} glosa={dados.glosa} />
      <LinkDoTrp competencia={competencia} fechada={fechada} />
    </>
  )

  return (
    <Colunas lateral={lateral}>
      <SumarioDaPagina secoes={SECOES} />

      <section id="indicadores" aria-labelledby="titulo-indicadores" className="scroll-mt-32 space-y-4">
        <h2 id="titulo-indicadores" className="titulo-da-secao">
          Indicadores de {competenciaPorExtenso(fechamento.competencia)}
        </h2>

        {/* O carimbo vem antes dos indicadores, e não depois: ele é a informação que muda a leitura
            de todos os números abaixo. Um fechamento apurado sobre coleta que não alcança o fim do
            mês sai com aparência de certo — foi o risco número um da análise, e é o único silencioso. */}
        <CarimboDeProcedencia
          origens={origens}
          coletaMaisRecente={fechamento.cobertura.coletaMaisRecente}
          ressalva={fechamento.cobertura.cobreACompetenciaInteira ? undefined : TEXTOS.fiscalizacao.coberturaIncompleta}
        />

        {/* Só aparece enquanto o IIE não tem número, e o que mostra depende de POR QUE ele não tem —
            ver `CartaoDoIie`. Mora junto dos indicadores porque é um deles esperando dado. */}
        {indicadorSemNumero(fechamento, 'IIE') && <CartaoDoIie competencia={competencia} dados={dados} />}

        {/* Lista, e não uma grade de `<div>`: são seis itens do mesmo tipo, e é assim que quem usa
            leitor de tela ouve "lista de 6 itens" antes de entrar no primeiro. */}
        <ul className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {fechamento.indicadores.map((indicador) => (
            <CartaoDeIndicador key={indicador.codigo} indicador={indicador} competencia={competencia} />
          ))}
        </ul>
      </section>

      <div id="desconto" className="scroll-mt-32">
        <RedutorDaFatura redutor={fechamento.redutor} />
      </div>

      {/* Cobertura, parecer do mês, alertas e suspeitas, nessa ordem — o contêiner guarda o porquê
          dela. Nada ali bloqueia o fechamento: as ações, na outra coluna, não dependem de nenhuma
          dessas consultas. */}
      <div id="analise" className="scroll-mt-32 space-y-5">
        <ConferenciaDoMes
          competencia={fechamento.competencia}
          cobertura={fechamento.coberturaDaAnalise}
          alertas={fechamento.alertasDaIa}
          suspeitas={fechamento.suspeitasDeFracionamento}
        />
      </div>

      {/* A conta e, logo depois, as regras: é quando discorda de um número que o fiscal vai aos
          critérios — e é aí que precisa achar a página do TR. */}
      <div id="conta" className="scroll-mt-32 space-y-5">
        <ContagensDoMes
          competencia={fechamento.competencia}
          contagens={fechamento.contagens}
          semJulgamento={fechamento.chamadosSemJulgamentoDoFiscal}
          foraDaApuracao={fechamento.chamadosForaDaApuracao}
        />
        <CriteriosDoTr parametros={dados.parametros} fechada={fechada} />
      </div>

      <div id="fontes" className="scroll-mt-32">
        {fontes}
      </div>
    </Colunas>
  )
}

/**
 * A grade das duas colunas. A lateral vem primeiro no DOM e vai para a direita só em tela larga.
 *
 * @param props o conteúdo da coluna dos atos e o da coluna principal
 * @returns a grade
 */
function Colunas({ lateral, children }: { lateral: ReactNode; children: ReactNode }) {
  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_21rem] xl:items-start">
      <aside aria-label="O mês e o que fazer com ele" className="space-y-4 xl:order-2">
        {lateral}
      </aside>
      <div className="min-w-0 space-y-8 xl:order-1">{children}</div>
    </div>
  )
}
